import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaCheck, FaClock, FaExclamationTriangle, FaFilter, FaTasks, FaTimes } from 'react-icons/fa';
import { XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar, Legend } from 'recharts';
import ErpTemplate from '../../components/ErpTemplate';
import { FinCheck, FinField } from '../../components/FinField';
import ReactSelect from '../../components/ReactSelect';
import api from '../../services/api';
import { TIPOS_EVENTO } from './eventoTipos';
import { useSetFullBreadcrumbTrail } from '../../context/BreadcrumbContext';
import '../../css/financeiro-parcela-pagar.css';

// Os tokens do design system, não hex solto: o gráfico precisa da cor como valor, e ela tem que
// ser a MESMA das pills e dos KPIs — senão a tela diz duas coisas sobre o mesmo estado.
const COR = { pendentes: 'var(--fin-wn)', atrasados: 'var(--fin-dg)', concluidos: 'var(--fin-ok)' };
// recharts não resolve var(); precisa do valor literal, e estes são os mesmos tokens acima.
const COR_GRAFICO = { Pendentes: '#a85b07', Atrasados: '#c1232b', 'Concluídos': '#15803d' };
// A classe do KPI espelha o token — ver .fin-pp__kpi-value.is-* no CSS.
const CLASSE_KPI = { pendentes: 'is-warn', atrasados: 'is-danger', concluidos: 'is-ok' };

// Partição fica na página (identifica QUAL SLA); casamento vai para o modal (define quais eventos
// ele pega). São perguntas de natureza diferente e por isso ocupam lugares diferentes.
const FILTRO_VAZIO = { sla: '', cliente: '', categoria: '', backoffice: '', tipo: '', complexidade: '' };
const CHAVES_MODAL = ['backoffice', 'tipo', 'complexidade'];

const ROTULO_CATEGORIA = { cliente: 'Cliente', access: 'Access', financeiro: 'Financeiro' };
const ROTULO_BACKOFFICE = { todos: 'Todos', terceiro: 'Terceiro', exclusivo: 'Exclusivo' };
const ROTULO_COMPLEXIDADE = { todos: 'Todas', baixa: 'Baixa', media: 'Média', alta: 'Alta' };

const rotuloTipoEvento = (valor) => TIPOS_EVENTO.find((t) => t.value === valor)?.label ?? valor;

export default function PainelSlaDashboard() {
    const token = localStorage.getItem('token');
    const setFullTrail = useSetFullBreadcrumbTrail();

    const [dados, setDados] = useState({ macro: false, slas: [], opcoes: {}, resumo: {} });
    const [carregando, setCarregando] = useState(true);
    const [filtro, setFiltro] = useState({ ...FILTRO_VAZIO });
    const [mostrarFiltros, setMostrarFiltros] = useState(false);
    const controllerRef = useRef(null);
    // Ligado por padrão: o gráfico abre com o quadro inteiro. Quem quiser comparar só pendente e
    // atrasado — que é onde a barra de concluído atrapalha a escala — desmarca.
    const [mostrarConcluidos, setMostrarConcluidos] = useState(true);

    // Quantos filtros estão ligados — vira o contador no botão, para a pessoa saber que a lista
    // está recortada mesmo com o modal fechado.
    // Só os do modal entram no contador: os da página já estão à vista, e contá-los faria o
    // badge aparecer por um filtro que a pessoa está olhando.
    const filtrosAtivos = CHAVES_MODAL.filter((c) => filtro[c] !== '' && filtro[c] != null).length;

    const buscar = (override) => {
        const atual = override ? { ...filtro, ...override } : filtro;
        const params = Object.fromEntries(Object.keys(FILTRO_VAZIO).map((c) => [c, atual[c]]));

        // Trocar filtro com a busca anterior em voo: a antiga é abortada e cai no `catch` como
        // ERR_CANCELED, que sai antes de tocar em estado. Vale desde o axios 0.27, que lê `signal`.
        if (controllerRef.current) {
            controllerRef.current.abort();
        }
        controllerRef.current = new AbortController();

        setCarregando(true);
        api({
            method: 'get',
            action: '/sla/dashboard',
            token,
            params,
            signal: controllerRef.current.signal,
        })
            .then((response) => {
                setDados(response.data.data ?? { macro: false, slas: [], opcoes: {}, resumo: {} });
                setCarregando(false);
            })
            .catch((error) => {
                if (error.code === 'ERR_CANCELED' || error.name === 'CanceledError') return;
                // Sem isto a tela vira "nenhum SLA" e o operador conclui que perdeu o acesso.
                toast.error('Erro ao carregar o painel: ' + (error.response?.data?.error ?? error.message ?? ''));
                setCarregando(false);
            });
    };

    useEffect(() => {
        // Sem a trilha propria o breadcrumb continua mostrando a tela anterior.
        setFullTrail([{ path: '/painelSla', label: 'Pendências' }]);
        buscar();
        // eslint-disable-next-line
    }, []);

    // As opções vêm do conjunto COMPLETO que o endpoint devolve em `opcoes`, não do resultado
    // filtrado — senão escolher um SLA apagaria os outros do select e prenderia a pessoa na
    // escolha. É também o que faz a tela escalar: um SLA mostra um, cinco mostram cinco.
    const opcoesSla = useMemo(
        () => (dados.opcoes?.slas ?? []).map((o) => ({ value: o.id, label: o.nome })),
        [dados.opcoes]
    );

    const opcoesCliente = useMemo(
        () => (dados.opcoes?.clientes ?? []).map((o) => ({ value: o.id, label: o.nome })),
        [dados.opcoes]
    );

    const opcoesCategoria = useMemo(
        () => (dados.opcoes?.categorias ?? []).map((v) => ({ value: v, label: ROTULO_CATEGORIA[v] ?? v })),
        [dados.opcoes]
    );

    const opcoesBackoffice = useMemo(
        () => (dados.opcoes?.backoffices ?? []).map((v) => ({ value: v, label: ROTULO_BACKOFFICE[v] ?? v })),
        [dados.opcoes]
    );

    const opcoesTipo = useMemo(
        () => (dados.opcoes?.tipos ?? []).map((v) => ({ value: v, label: rotuloTipoEvento(v) })),
        [dados.opcoes]
    );

    const opcoesComplexidade = useMemo(
        () => (dados.opcoes?.complexidades ?? []).map((v) => ({ value: v, label: ROTULO_COMPLEXIDADE[v] ?? v })),
        [dados.opcoes]
    );

    // Admin e diretor veem todos os filtros sempre. Para os demais, select de uma opção só é
    // decoração — aparece apenas quando há de fato o que escolher.
    const mostrarFiltro = (opcoes) => dados.macro || opcoes.length > 1;

    // Conta um grupo, aceitando tanto lista (pendentes/atrasados) quanto número (concluidos).
    const contar = (alerta, chave) => (Array.isArray(alerta[chave]) ? alerta[chave].length : (alerta[chave] ?? 0));

    // Com vários SLAs a comparação útil é entre eles; com UM só, é entre os alertas dele. Sem essa
    // troca o gráfico repetiria exatamente os três números dos KPIs — e era por isso que ele
    // sumia ao filtrar, o que deixava a tela encolher justamente quando a pessoa se aprofundava.
    const umSlaSo = (dados.slas ?? []).length === 1;

    const dadosGrafico = useMemo(() => {
        const montar = (nome, alertas) => ({
            nome,
            Pendentes: alertas.reduce((t, a) => t + contar(a, 'pendentes'), 0),
            Atrasados: alertas.reduce((t, a) => t + contar(a, 'atrasados'), 0),
            'Concluídos': alertas.reduce((t, a) => t + contar(a, 'concluidos'), 0),
        });

        if (umSlaSo) {
            return (dados.slas[0].alertas ?? []).map((a) => montar(a.nome, [a]));
        }

        return (dados.slas ?? []).map((sla) => montar(sla.descricao, sla.alertas ?? []));
        // eslint-disable-next-line
    }, [dados.slas, umSlaSo]);

    // Ranking do que está mais tempo vencido, atravessando SLAs e alertas.
    const maisAtrasados = useMemo(() => {
        const itens = [];

        (dados.slas ?? []).forEach((sla) => {
            (sla.alertas ?? []).forEach((alerta) => {
                (alerta.atrasados ?? []).forEach((evento) => {
                    itens.push({ ...evento, sla: sla.descricao, alerta: alerta.nome });
                });
            });
        });

        return itens.sort((a, b) => (b.dias ?? 0) - (a.dias ?? 0)).slice(0, 10);
    }, [dados.slas]);

    // Nenhum filtro ligado E nenhum SLA = a pessoa não tem acesso. Com filtro ligado, vazio
    // significa só que o recorte não achou nada — mensagem diferente.
    // Os filtros da página aplicam no próprio clique. O override é obrigatório: o setFiltro é
    // assíncrono, e sem ele a busca sairia com o valor anterior.
    const aplicar = (mudanca) => {
        setFiltro((st) => ({ ...st, ...mudanca }));
        buscar(mudanca);
    };

    const semFiltro = Object.keys(FILTRO_VAZIO).every((c) => !filtro[c]);
    const semAcesso = !carregando && (dados.slas ?? []).length === 0 && semFiltro;

    return (
        <ErpTemplate>
            <div className="main-content container-fluid fin-pp">
                <div className="fin-pp__head">
                    <div>
                        <h1 className="fin-pp__title">Pendências</h1>
                        <p className="fin-pp__subtitle">
                            {dados.macro
                                ? 'Visão geral de todos os SLAs do cliente.'
                                : 'Os SLAs em que você é destinatário de alerta.'}
                        </p>
                    </div>
                    {dados.macro && (
                        <div className="fin-pp__actions">
                            <span className="fin-pp__pill is-info">visão macro</span>
                        </div>
                    )}
                </div>

                {carregando ? (
                    <div className="fin-pp__card" style={{ padding: 32, textAlign: 'center' }}>
                        <div className="fin-pp__loading"><span className="fin-pp__spin" /></div>
                    </div>
                ) : semAcesso ? (
                    <div className="fin-pp__card" style={{ padding: 32, textAlign: 'center' }}>
                        <FaTasks size={28} style={{ opacity: 0.35, marginBottom: 10 }} />
                        <h2 className="fin-pp__cphead-title">Você não tem SLA atribuído</h2>
                        <p className="fin-pp__subtitle" style={{ marginTop: 6 }}>
                            Este painel mostra os SLAs em que você é destinatário de algum alerta.
                            Fale com um administrador para ser incluído.
                        </p>
                    </div>
                ) : (
                    <>
                        {/* Três números primeiro: é o que se lê de relance. O detalhe vem depois. */}
                        <section className="fin-pp__kpis" style={{ marginBottom: 14 }}>
                            {[
                                { chave: 'atrasados', rotulo: 'Atrasados', icone: <FaExclamationTriangle size={15} /> },
                                { chave: 'pendentes', rotulo: 'Pendentes', icone: <FaClock size={15} /> },
                                { chave: 'concluidos', rotulo: 'Concluídos', icone: <FaCheck size={15} /> },
                            ].map((kpi) => (
                                <div key={kpi.chave} className="fin-pp__kpi">
                                    <div className="fin-pp__kpi-top">
                                        <span className="fin-pp__kpi-label">{kpi.rotulo}</span>
                                        <span className="fin-pp__kpi-ic" style={{ color: COR[kpi.chave] }}>{kpi.icone}</span>
                                    </div>
                                    <div className={'fin-pp__kpi-value ' + CLASSE_KPI[kpi.chave]}>
                                        {dados.resumo?.[kpi.chave] ?? 0}
                                    </div>
                                </div>
                            ))}
                        </section>

                        {/* Partição na página: identifica QUAL SLA se está olhando, então fica à
                            vista. Os eixos de casamento vão para o modal. */}
                        {(mostrarFiltro(opcoesSla) || mostrarFiltro(opcoesCliente) || mostrarFiltro(opcoesCategoria)) && (
                            <div className="fin-pp__card" style={{ padding: '12px 16px', marginBottom: 16 }}>
                                <div className="fin-pp__filters" style={{ alignItems: 'flex-end' }}>
                                    {mostrarFiltro(opcoesCliente) && (
                                        <FinField label="Cliente">
                                            <ReactSelect
                                                isClearable
                                                value={opcoesCliente.find((o) => o.value === filtro.cliente) ?? null}
                                                onChange={(e) => aplicar({ cliente: e?.value ?? '' })}
                                                options={opcoesCliente}
                                                key={'cli-' + opcoesCliente.length}
                                            />
                                        </FinField>
                                    )}
                                    {mostrarFiltro(opcoesSla) && (
                                        <FinField label="SLA">
                                            <ReactSelect
                                                isClearable
                                                value={opcoesSla.find((o) => o.value === filtro.sla) ?? null}
                                                onChange={(e) => aplicar({ sla: e?.value ?? '' })}
                                                options={opcoesSla}
                                                key={'sla-' + opcoesSla.length}
                                            />
                                        </FinField>
                                    )}
                                    {mostrarFiltro(opcoesCategoria) && (
                                        <FinField label="Categoria">
                                            <ReactSelect
                                                isClearable
                                                value={opcoesCategoria.find((o) => o.value === filtro.categoria) ?? null}
                                                onChange={(e) => aplicar({ categoria: e?.value ?? '' })}
                                                options={opcoesCategoria}
                                                key={'cat-' + opcoesCategoria.length}
                                            />
                                        </FinField>
                                    )}

                                    {/* Envolvido em fin-pp__field--auto: .fin-pp__filters é flex com wrap
                                        desenhado para filhos .fin-pp__field (flex 1 1 200px). Botão cru
                                        participa do wrap sem a caixa de label dos vizinhos e cai sozinho
                                        numa linha, desalinhado, em viewport estreita. */}
                                    <div className="fin-pp__field fin-pp__field--auto">
                                        <button type="button" className="fin-pp__btn" onClick={() => setMostrarFiltros(true)}>
                                            <FaFilter size={14} /> Filtros
                                            {filtrosAtivos > 0 && (
                                                <span className="fin-pp__pill is-info" style={{ marginLeft: 6 }}>
                                                    {filtrosAtivos}
                                                </span>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {mostrarFiltros && (
                            <div
                                className="modal fade show d-block fin-pp fin-pp__modalbg"
                                tabIndex="-1"
                                onClick={(e) => { if (e.target === e.currentTarget) setMostrarFiltros(false); }}
                            >
                                <div className="modal-dialog modal-dialog-centered modal-lg">
                                    <div className="modal-content fin-pp__modal">
                                        <div className="fin-pp__modal-head">
                                            <span className="fin-pp__modal-head-ic"><FaFilter size={17} /></span>
                                            <h3 className="fin-pp__modal-title">Filtros
                                                <span className="fin-pp__modal-sub">Os eixos que definem quais eventos cada SLA alcança.</span>
                                            </h3>
                                            <button type="button" className="fin-pp__modal-close" onClick={() => setMostrarFiltros(false)}>
                                                <FaTimes size={16} />
                                            </button>
                                        </div>

                                        <div className="modal-body">
                                            <div className="fin-pp__filters">
                                                {mostrarFiltro(opcoesBackoffice) && (
                                                    <FinField label="Backoffice">
                                                        <ReactSelect
                                                            isClearable
                                                            value={opcoesBackoffice.find((o) => o.value === filtro.backoffice) ?? null}
                                                            onChange={(e) => setFiltro((st) => ({ ...st, backoffice: e?.value ?? '' }))}
                                                            options={opcoesBackoffice}
                                                            key={'bkf-' + opcoesBackoffice.length}
                                                        />
                                                    </FinField>
                                                )}
                                                {mostrarFiltro(opcoesTipo) && (
                                                    <FinField label="Tipo de evento">
                                                        <ReactSelect
                                                            isClearable
                                                            value={opcoesTipo.find((o) => o.value === filtro.tipo) ?? null}
                                                            onChange={(e) => setFiltro((st) => ({ ...st, tipo: e?.value ?? '' }))}
                                                            options={opcoesTipo}
                                                            key={'tip-' + opcoesTipo.length}
                                                        />
                                                    </FinField>
                                                )}
                                                {mostrarFiltro(opcoesComplexidade) && (
                                                    <FinField label="Complexidade">
                                                        <ReactSelect
                                                            isClearable
                                                            value={opcoesComplexidade.find((o) => o.value === filtro.complexidade) ?? null}
                                                            onChange={(e) => setFiltro((st) => ({ ...st, complexidade: e?.value ?? '' }))}
                                                            options={opcoesComplexidade}
                                                            key={'cpx-' + opcoesComplexidade.length}
                                                        />
                                                    </FinField>
                                                )}
                                            </div>
                                        </div>

                                        <div className="fin-pp__modal-foot">
                                            <button
                                                type="button"
                                                className="fin-pp__btn"
                                                onClick={() => {
                                                    // Limpa só os do modal: os da página estão à vista e apagá-los
                                                    // junto seria desfazer uma escolha que a pessoa não pediu.
                                                    const limpo = { backoffice: '', tipo: '', complexidade: '' };
                                                    setFiltro((st) => ({ ...st, ...limpo }));
                                                    buscar(limpo);
                                                }}
                                            >
                                                Limpar filtros
                                            </button>
                                            <button
                                                type="button"
                                                className="fin-pp__btn fin-pp__btn--primary"
                                                onClick={() => { setMostrarFiltros(false); buscar(); }}
                                            >
                                                Aplicar filtros
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {dadosGrafico.length > 0 && (
                            <div className="fin-pp__card" style={{ padding: 16, marginBottom: 16 }}>
                                <div className="fin-pp__cphead">
                                    <span className="fin-pp__cphead-title">{umSlaSo ? 'Por alerta' : 'Por SLA'}</span>
                                    <span className="fin-pp__cphead-sub">
                                        {umSlaSo
                                            ? 'Onde estão as pendências dentro deste SLA.'
                                            : 'Comparação entre os SLAs visíveis.'}
                                    </span>
                                    <div style={{ marginLeft: 'auto' }}>
                                        <FinCheck
                                            id="mostrarConcluidos"
                                            label="Exibir concluídos"
                                            checked={mostrarConcluidos}
                                            onChange={(e) => setMostrarConcluidos(e.target.checked)}
                                        />
                                    </div>
                                </div>
                                <div style={{ width: '100%', height: 260 }}>
                                    <ResponsiveContainer>
                                        <BarChart data={dadosGrafico}>
                                            <XAxis dataKey="nome" tick={{ fontSize: 10, fill: '#4E5057' }} axisLine={false} tickLine={false} />
                                            <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: '#4E5057' }} axisLine={false} tickLine={false} />
                                            <Tooltip />
                                            <Legend />
                                            <Bar dataKey="Atrasados" fill={COR_GRAFICO.Atrasados} />
                                            <Bar dataKey="Pendentes" fill={COR_GRAFICO.Pendentes} />
                                            {mostrarConcluidos && <Bar dataKey="Concluídos" fill={COR_GRAFICO["Concluídos"]} />}
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>
                        )}

                        {maisAtrasados.length > 0 && (
                            <div className="fin-pp__card" style={{ overflow: 'hidden', marginBottom: 16 }}>
                                <div className="fin-pp__cphead" style={{ padding: '12px 16px' }}>
                                    <span className="fin-pp__cphead-title">Mais atrasados</span>
                                    <span className="fin-pp__cphead-sub">Ordenado pelos dias além do prazo.</span>
                                </div>
                                <div className="fin-pp__tblwrap">
                                    <table className="fin-pp__tbl">
                                        <thead>
                                            <tr>
                                                <th>Evento</th>
                                                <th>SLA</th>
                                                <th>Alerta</th>
                                                <th>Prazo</th>
                                                <th>Dias</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {maisAtrasados.map((item) => (
                                                <tr key={item.sla + item.id + item.alerta}>
                                                    <td>
                                                        <Link to={`/evento?idEvento=${item.id}`}>{item.nome}</Link>
                                                    </td>
                                                    <td className="fin-pp__cell-muted">{item.sla}</td>
                                                    <td className="fin-pp__cell-muted">{item.alerta}</td>
                                                    <td className="fin-pp__cell-muted">
                                                        {item.data ? item.data.split('-').reverse().join('/') : '—'}
                                                    </td>
                                                    <td>
                                                        <span className="fin-pp__pill is-danger">{item.dias}</span>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        {/* Um bloco por SLA, e dentro dele um por alerta. É o que dá a leitura
                            "onde está o problema" sem precisar de filtro. */}
                        {(dados.slas ?? []).map((sla) => (
                            <div key={sla.id} className="fin-pp__card" style={{ padding: 16, marginBottom: 16 }}>
                                <div className="fin-pp__cphead">
                                    <span className="fin-pp__cphead-title">{sla.descricao}</span>
                                    <span className="fin-pp__cphead-sub">
                                        {sla.cliente} · {sla.categoria}
                                    </span>
                                </div>

                                <div className="fin-pp__tblwrap">
                                    <table className="fin-pp__tbl">
                                        <thead>
                                            <tr>
                                                <th>Alerta</th>
                                                <th>Atrasados</th>
                                                <th>Pendentes</th>
                                                <th>Concluídos</th>
                                                <th>Sem prazo</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(sla.alertas ?? []).map((alerta) => (
                                                <tr key={alerta.id}>
                                                    <td>{alerta.nome}</td>
                                                    <td>
                                                        {alerta.atrasados.length > 0
                                                            ? <span className="fin-pp__pill is-danger">{alerta.atrasados.length}</span>
                                                            : <span className="fin-pp__cell-muted">0</span>}
                                                    </td>
                                                    <td>
                                                        {alerta.pendentes.length > 0
                                                            ? <span className="fin-pp__pill is-warn">{alerta.pendentes.length}</span>
                                                            : <span className="fin-pp__cell-muted">0</span>}
                                                    </td>
                                                    <td>
                                                        {alerta.concluidos > 0
                                                            ? <span className="fin-pp__pill is-ok">{alerta.concluidos}</span>
                                                            : <span className="fin-pp__cell-muted">0</span>}
                                                    </td>
                                                    <td className="fin-pp__cell-muted">{alerta.naoSeAplica}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        ))}
                    </>
                )}
            </div>
        </ErpTemplate>
    );
}
